import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CustomCircularProgress from "src/Component/custom/CustomCircularProgress";
import { useSnackbar } from "src/Component/Snakbar";
import {
  createProfileBuildingAsync,
  getProfileBuildingAsync,
  updateProfileBuildingAsync,
} from "src/redux/services/studyAbroadServices";
import * as Yup from "yup";

// ✅ Custom validation functions
const validatePercentage = (value) => {
  if (!value) return true; // Allow empty values for optional fields

  // Check for % sign
  if (value.includes("%")) {
    return "Percentage symbol (%) is not allowed. Please enter only numbers (e.g., 85.5 instead of 85.5%).";
  }

  // Check for alphabets and special characters (allow decimal point and numbers)
  if (!/^-?\d*\.?\d*$/.test(value)) {
    return "Only numbers and decimal points are allowed (e.g., 85.5, 90.25).";
  }

  // Check if value is a valid number between 0 and 100
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return "Please enter a valid number.";
  }
  if (numValue < 0 || numValue > 100) {
    return "Percentage must be between 0 and 100.";
  }

  return true;
};

const validateCGPA = (value) => {
  if (!value) return true; // Allow empty values for optional fields

  // Check for % sign
  if (value.includes("%")) {
    return "Percentage symbol (%) is not allowed for CGPA. Please enter only numbers (e.g., 8.5 instead of 8.5%).";
  }

  // Check for alphabets and special characters (allow decimal point and numbers)
  if (!/^-?\d*\.?\d*$/.test(value)) {
    return "Only numbers and decimal points are allowed for CGPA (e.g., 8.5, 9.2).";
  }

  // Check if value is a valid number between 0 and 10
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return "Please enter a valid CGPA.";
  }
  if (numValue < 0 || numValue > 10) {
    return "CGPA must be between 0 and 10.";
  }

  // Validate decimal precision (optional: limit to 2 decimal places)
  const decimalPlaces = value.split(".")[1]?.length || 0;
  if (decimalPlaces > 2) {
    return "CGPA should have maximum 2 decimal places.";
  }

  return true;
};

const validateJeeRank = (value) => {
  if (!value) return true; // Allow empty values for optional fields

  // Check for % sign
  if (value.includes("%")) {
    return "Percentage symbol (%) is not allowed for rank. Please enter only numbers.";
  }

  // Check for alphabets and special characters (only allow numbers for rank)
  if (!/^\d+$/.test(value)) {
    return "Only whole numbers are allowed for rank (e.g., 1500, 5000).";
  }

  // Check if value is a valid positive integer
  const numValue = parseInt(value);
  if (isNaN(numValue) || numValue <= 0) {
    return "Please enter a valid positive rank number.";
  }

  return true;
};

// ✅ Enhanced input formatting for better UX
const formatNumericInput = (value, fieldType) => {
  if (!value) return value;

  // Remove any % signs immediately
  value = value.replace(/%/g, "");

  // For percentage and CGPA fields, allow decimals
  if (fieldType === "percentage" || fieldType === "cgpa") {
    // Allow only numbers and one decimal point
    value = value.replace(/[^\d.]/g, "");

    // Ensure only one decimal point
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }

    // Limit decimal places to 2
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + "." + parts[1].substring(0, 2);
    }
  }

  // For rank fields, only allow whole numbers
  if (fieldType === "rank") {
    value = value.replace(/\D/g, "");
  }

  return value;
};

// ✅ Yup Validation Schema
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  city: Yup.string().required("City is required"),
  institution: Yup.string().required("Institution is required"),
  currentCourse: Yup.string().required("Current course is required"),
  intake: Yup.string(),
  careerGoals: Yup.string().required("Career goal is required"),
  overall10: Yup.string()
    .required("10th aggregate is required")
    .test("percentage-10", "Invalid 10th percentage", validatePercentage),
  overall12: Yup.string()
    .required("12th aggregate is required")
    .test("percentage-12", "Invalid 12th percentage", validatePercentage),
  graduationCgpa: Yup.string().test("cgpa", "Invalid CGPA", validateCGPA),
  jeeRank: Yup.string().test("jee-rank", "Invalid JEE Rank", validateJeeRank),
  resume: Yup.mixed().test("required", "Resume is required", (value) => {
    return value instanceof File || typeof value === "string";
  }),
});

const StudentCounsellorForm = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isFieldsDisabled, setIsFieldsDisabled] = useState(false);
  const [isSaveChangesClicked, setIsSaveChangesClicked] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({}); // Track specific field errors

  const { isProfileCreated, profileData, isLoading, isSubmitting } =
    useSelector((state) => state.studyAbroad);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: "",
      city: "",
      institution: "",
      currentCourse: "",
      intake: "",
      careerGoals: "",
      overall10: "",
      overall12: "",
      graduationCgpa: "",
      relevantCourses: "",
      professionalDegrees: "",
      honoursAwards: "",
      jeeRank: "",
      researchProjects: "",
      publications: "",
      researchAssistantships: "",
      projects: "",
      fullTimeJobs: "",
      internships: "",
      partTimeJobs: "",
      certifications: "",
      sports: "",
      musicDance: "",
      arts: "",
      competitions: "",
      clubs: "",
      communityServices: "",
      leadership: "",
      technicalSkills: "",
      softSkills: "",
      languageSkills: "",
      linkedin: "",
      personalWebsite: "",
      socialMedia: "",
      financialPosition: "",
      resume: null,
    },
  });

  // ✅ Enhanced validation before submission
  const validateFormFields = (data) => {
    const newErrors = {};

    // Validate numeric fields
    if (data.overall10) {
      const error = validatePercentage(data.overall10);
      if (error !== true) newErrors.overall10 = error;
    }

    if (data.overall12) {
      const error = validatePercentage(data.overall12);
      if (error !== true) newErrors.overall12 = error;
    }

    if (data.graduationCgpa) {
      const error = validateCGPA(data.graduationCgpa);
      if (error !== true) newErrors.graduationCgpa = error;
    }

    if (data.jeeRank) {
      const error = validateJeeRank(data.jeeRank);
      if (error !== true) newErrors.jeeRank = error;
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (data) => {
    // Validate form before submission
    if (!validateFormFields(data)) {
      showSnackbar(
        "Please fix the validation errors before submitting.",
        "error",
      );
      return;
    }

    let base64Resume = null;

    // Check if the resume is a File object before converting
    if (data.resume instanceof File) {
      base64Resume = await toBase64(data.resume);
    } else {
      base64Resume = data.resume;
    }

    const payload = {
      name: data.name,
      city: data.city,
      institute: data.institution,
      currentCourse: data.currentCourse,
      intake: data.intake,
      careerGoal: data.careerGoals,
      academicPreparation: {
        tenthOverallAggregate: data.overall10,
        twelfthOverallAggregate: data.overall12,
        graduationCgpa: data.graduationCgpa,
        relevantCompletedCourses: data.relevantCourses,
        professionalDegrees: data.professionalDegrees,
        honoursAndAwards: data.honoursAwards,
        others: data.jeeRank,
      },
      researchExperience: {
        researchProjects: data.researchProjects,
        publications: data.publications,
        researchAssistantships: data.researchAssistantships,
      },
      projects: data.projects,
      workExperience: {
        fullTimeJobExperience: data.fullTimeJobs,
        internships: data.internships,
        partTimeJobs: data.partTimeJobs,
        professinalCertifications: data.certifications,
      },
      extracurricularActivites: {
        sports: data.sports,
        musicAndDance: data.musicDance,
        arts: data.arts,
        competitionAndEvents: data.competitions,
        participationInClubsAndSocieties: data.clubs,
        communityServices: data.communityServices,
      },
      skillsAndCompetencies: {
        leadershipRoles: data.leadership,
        technicalSkills: data.technicalSkills,
        softSkills: data.softSkills,
        languageSkills: data.languageSkills,
      },
      onlinePresence: {
        linkedInProfile: data.linkedin,
        personalWebsite: data.personalWebsite,
        professionalSocialMedia: data.socialMedia,
      },
      financialPosition: data.financialPosition,
      resume: base64Resume,
    };

    try {
      if (isProfileCreated && isEditMode && isSaveChangesClicked) {
        await dispatch(updateProfileBuildingAsync(payload)).unwrap();
        showSnackbar("Profile updated successfully!", "success");
        setIsSaveChangesClicked(false);
        setIsFieldsDisabled(true);
        setIsEditMode(false);
      } else if (!isProfileCreated) {
        const res = await dispatch(
          createProfileBuildingAsync(payload),
        ).unwrap();

        if (res?.success) {
          showSnackbar("Profile created successfully!", "success");
        } else {
          showSnackbar(
            res?.message || "Something went wrong during creation.",
            "error",
          );
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      showSnackbar(
        "Failed to submit profile. Please check all fields and try again.",
        "error",
      );
    }
  };

  useEffect(() => {
    dispatch(getProfileBuildingAsync());
  }, [dispatch]);

  useEffect(() => {
    if (profileData) {
      reset({
        name: profileData.name,
        city: profileData.city,
        institution: profileData.institute,
        currentCourse: profileData.currentCourse,
        intake: profileData.intake,
        careerGoals: profileData.careerGoal,
        overall10: profileData.academicPreparation?.tenthOverallAggregate || "",
        overall12:
          profileData.academicPreparation?.twelfthOverallAggregate || "",
        graduationCgpa: profileData.academicPreparation?.graduationCgpa || "",
        relevantCourses:
          profileData.academicPreparation?.relevantCompletedCourses || "",
        professionalDegrees:
          profileData.academicPreparation?.professionalDegrees || "",
        honoursAwards: profileData.academicPreparation?.honoursAndAwards || "",
        jeeRank: profileData.academicPreparation?.others || "",
        researchProjects:
          profileData.researchExperience?.researchProjects || "",
        publications: profileData.researchExperience?.publications || "",
        researchAssistantships:
          profileData.researchExperience?.researchAssistantships || "",
        projects: profileData.projects || "",
        fullTimeJobs: profileData.workExperience?.fullTimeJobExperience || "",
        internships: profileData.workExperience?.internships || "",
        partTimeJobs: profileData.workExperience?.partTimeJobs || "",
        certifications:
          profileData.workExperience?.professinalCertifications || "",
        sports: profileData.extracurricularActivites?.sports || "",
        musicDance: profileData.extracurricularActivites?.musicAndDance || "",
        arts: profileData.extracurricularActivites?.arts || "",
        competitions:
          profileData.extracurricularActivites?.competitionAndEvents || "",
        clubs:
          profileData.extracurricularActivites
            ?.participationInClubsAndSocieties || "",
        communityServices:
          profileData.extracurricularActivites?.communityServices || "",
        leadership: profileData.skillsAndCompetencies?.leadershipRoles || "",
        technicalSkills:
          profileData.skillsAndCompetencies?.technicalSkills || "",
        softSkills: profileData.skillsAndCompetencies?.softSkills || "",
        languageSkills: profileData.skillsAndCompetencies?.languageSkills || "",
        linkedin: profileData.onlinePresence?.linkedInProfile || "",
        personalWebsite: profileData.onlinePresence?.personalWebsite || "",
        socialMedia: profileData.onlinePresence?.professionalSocialMedia || "",
        financialPosition: profileData.financialPosition || "",
        resume: profileData.resume || "",
      });
      setIsFieldsDisabled(true);
      setIsEditMode(false);
      setFieldErrors({}); // Clear any previous errors
    }
  }, [profileData, reset]);

  // Helper function to convert file to Base64
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleEditClick = () => {
    setIsFieldsDisabled(false);
    setIsEditMode(true);
    setFieldErrors({}); // Clear errors when editing
  };

  const handleCancelClick = () => {
    setIsFieldsDisabled(true);
    setIsEditMode(false);
    setFieldErrors({}); // Clear errors when canceling
    // Reset form to original values
    if (profileData) {
      reset({
        name: profileData.name,
        city: profileData.city,
        institution: profileData.institute,
        currentCourse: profileData.currentCourse,
        intake: profileData.intake,
        careerGoals: profileData.careerGoal,
        overall10: profileData.academicPreparation?.tenthOverallAggregate || "",
        overall12:
          profileData.academicPreparation?.twelfthOverallAggregate || "",
        graduationCgpa: profileData.academicPreparation?.graduationCgpa || "",
        relevantCourses:
          profileData.academicPreparation?.relevantCompletedCourses || "",
        professionalDegrees:
          profileData.academicPreparation?.professionalDegrees || "",
        honoursAwards: profileData.academicPreparation?.honoursAndAwards || "",
        jeeRank: profileData.academicPreparation?.others || "",
        researchProjects:
          profileData.researchExperience?.researchProjects || "",
        publications: profileData.researchExperience?.publications || "",
        researchAssistantships:
          profileData.researchExperience?.researchAssistantships || "",
        projects: profileData.projects || "",
        fullTimeJobs: profileData.workExperience?.fullTimeJobExperience || "",
        internships: profileData.workExperience?.internships || "",
        partTimeJobs: profileData.workExperience?.partTimeJobs || "",
        certifications:
          profileData.workExperience?.professinalCertifications || "",
        sports: profileData.extracurricularActivites?.sports || "",
        musicDance: profileData.extracurricularActivites?.musicAndDance || "",
        arts: profileData.extracurricularActivites?.arts || "",
        competitions:
          profileData.extracurricularActivites?.competitionAndEvents || "",
        clubs:
          profileData.extracurricularActivites
            ?.participationInClubsAndSocieties || "",
        communityServices:
          profileData.extracurricularActivites?.communityServices || "",
        leadership: profileData.skillsAndCompetencies?.leadershipRoles || "",
        technicalSkills:
          profileData.skillsAndCompetencies?.technicalSkills || "",
        softSkills: profileData.skillsAndCompetencies?.softSkills || "",
        languageSkills: profileData.skillsAndCompetencies?.languageSkills || "",
        linkedin: profileData.onlinePresence?.linkedInProfile || "",
        personalWebsite: profileData.onlinePresence?.personalWebsite || "",
        socialMedia: profileData.onlinePresence?.professionalSocialMedia || "",
        financialPosition: profileData.financialPosition || "",
        resume: profileData.resume || "",
      });
    }
  };

  const handleSaveChangesClick = () => {
    setIsSaveChangesClicked(true);
  };

  // ✅ Enhanced input renderer with real-time validation and formatting
  const renderInput = (name, label, multiline = false, type = "text") => {
    const isNumericField = [
      "overall10",
      "overall12",
      "graduationCgpa",
      "jeeRank",
    ].includes(name);
    const fieldType =
      name === "jeeRank"
        ? "rank"
        : name === "graduationCgpa"
        ? "cgpa"
        : "percentage";

    return (
      <Grid item xs={12} sm={6}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <TextField
              size="small"
              variant="standard"
              {...field}
              label={label}
              fullWidth
              multiline={multiline}
              type={type}
              disabled={isFieldsDisabled}
              error={!!errors[name] || !!fieldErrors[name]}
              helperText={errors[name]?.message || fieldErrors[name]}
              onChange={(e) => {
                let value = e.target.value;

                // Auto-format numeric inputs
                if (isNumericField) {
                  value = formatNumericInput(value, fieldType);
                }

                field.onChange(value);

                // Clear field-specific error when user starts typing
                if (fieldErrors[name] && value) {
                  setFieldErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                  });
                }
              }}
              onBlur={(e) => {
                // Validate on blur for numeric fields
                if (isNumericField && e.target.value) {
                  let validationResult = true;

                  if (name === "overall10" || name === "overall12") {
                    validationResult = validatePercentage(e.target.value);
                  } else if (name === "graduationCgpa") {
                    validationResult = validateCGPA(e.target.value);
                  } else if (name === "jeeRank") {
                    validationResult = validateJeeRank(e.target.value);
                  }

                  if (validationResult !== true) {
                    setFieldErrors((prev) => ({
                      ...prev,
                      [name]: validationResult,
                    }));
                  } else {
                    setFieldErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors[name];
                      return newErrors;
                    });
                  }
                }
              }}
              inputProps={{
                // Add input mode for better mobile experience
                inputMode: isNumericField
                  ? fieldType === "rank"
                    ? "numeric"
                    : "decimal"
                  : "text",
                pattern: isNumericField
                  ? fieldType === "rank"
                    ? "[0-9]*"
                    : "[0-9.]*"
                  : null,
              }}
            />
          )}
        />
      </Grid>
    );
  };

  const renderResumeField = () => (
    <Grid item xs={12}>
      <Controller
        name="resume"
        control={control}
        render={({ field }) => (
          <>
            <TextField
              type="file"
              fullWidth
              onChange={(e) => field.onChange(e.target.files[0])}
              disabled={isFieldsDisabled}
              error={!!errors.resume}
              helperText={errors.resume?.message}
            />
            {field.value &&
              typeof field.value === "string" &&
              !field.value.startsWith("data:") && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  <span style={{ color: "black", fontWeight: 500 }}>
                    {" "}
                    Uploaded File:{" "}
                  </span>{" "}
                  {field.value && field.value.slice(8, 100)}...
                </Typography>
              )}
          </>
        )}
      />
    </Grid>
  );

  return isLoading ? (
    <CustomCircularProgress />
  ) : (
    <Box sx={{ p: 1.5, textAlign: "left" }}>
      {profileData?.remark && (
        <Box
          sx={{
            mb: 3,
            p: 3,
            bgcolor: "#E3F2FD",
            borderRadius: 3,
            borderLeft: "6px solid #1976D2",
            boxShadow: 1,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            color="primary"
          >
            Counsellor's Feedback
          </Typography>

          <Typography
            variant="body1"
            color="text.primary"
            sx={{ mb: profileData.timeline ? 2 : 0, fontSize: 14 }}
          >
            {profileData.remark}
          </Typography>

          {profileData.timeline && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                fontWeight="medium"
                color="text.secondary"
              >
                Editable until:
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="error">
                {new Date(profileData.timeline).toLocaleString("en-IN", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </Typography>
            </Stack>
          )}
        </Box>
      )}

      {/* ✅ Validation Summary Alert */}
      {Object.keys(fieldErrors).length > 0 && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "#FFEBEE",
            borderRadius: 2,
            borderLeft: "6px solid #F44336",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Validation Errors
          </Typography>
          <Typography variant="body2" color="error">
            Please fix the following errors in the highlighted fields:
          </Typography>
          <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
            {Object.entries(fieldErrors).map(([fieldName, error]) => (
              <li key={fieldName}>
                <Typography variant="body2" color="error">
                  <strong>{fieldName}:</strong> {error}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <>
          {/* 1. Personal Details */}
          <Typography variant="h6" mb={1.5}>
            1. Personal Details
          </Typography>
          <Grid container spacing={2}>
            {renderInput("name", "Name *")}
            {renderInput("city", "City *")}
            {renderInput("institution", "Institution *")}
            {renderInput("currentCourse", "Current Course *")}
            {renderInput("intake", "Intake")}
          </Grid>

          {/* 2. Career Goals */}
          <Typography variant="h6" mt={3} mb={1.5}>
            2. Career Goals
          </Typography>
          <Grid container spacing={2}>
            {renderInput("careerGoals", "Career Goals *", true)}
          </Grid>

          {/* 3. Academic Preparation */}
          <Typography variant="h6" mt={3} mb={1.5}>
            3. Academic Preparation
          </Typography>
          <Grid container spacing={2}>
            {renderInput("overall10", "10th Overall Aggregate *")}
            {renderInput("overall12", "12th Overall Aggregate *")}
            {renderInput("graduationCgpa", "Graduation CGPA")}
            {renderInput("relevantCourses", "Relevant Completed Courses")}
            {renderInput("professionalDegrees", "Professional Degrees")}
            {renderInput("honoursAwards", "Honours & Awards")}
            {renderInput("jeeRank", "JEE Rank/Olympiads")}
          </Grid>

          {/* 4. Research Experience */}
          <Typography variant="h6" mt={3} mb={1.5}>
            4. Research Experience
          </Typography>
          <Grid container spacing={2}>
            {renderInput("researchProjects", "Research Projects")}
            {renderInput("publications", "Publications")}
            {renderInput("researchAssistantships", "Research Assistantships")}
          </Grid>

          {/* 5. Projects */}
          <Typography variant="h6" mt={3} mb={1.5}>
            5. Projects
          </Typography>
          <Grid container spacing={2}>
            {renderInput("projects", "Projects")}
          </Grid>

          {/* 6. Work Experience */}
          <Typography variant="h6" mt={3} mb={1.5}>
            6. Work Experience
          </Typography>
          <Grid container spacing={2}>
            {renderInput("fullTimeJobs", "Full-time Jobs")}
            {renderInput("internships", "Internships")}
            {renderInput("partTimeJobs", "Part-time Jobs")}
            {renderInput("certifications", "Professional Certifications")}
          </Grid>

          {/* 7. Extracurricular Activities */}
          <Typography variant="h6" mt={3} mb={1.5}>
            7. Extracurricular Activities
          </Typography>
          <Grid container spacing={2}>
            {renderInput("sports", "Sports")}
            {renderInput("musicDance", "Music & Dance")}
            {renderInput("arts", "Arts")}
            {renderInput("competitions", "Competitions & Events")}
            {renderInput("clubs", "Participation in Clubs & Societies")}
            {renderInput("communityServices", "Community Services")}
          </Grid>

          {/* 8. Skills & Competencies */}
          <Typography variant="h6" mt={3} mb={1.5}>
            8. Skills & Competencies
          </Typography>
          <Grid container spacing={2}>
            {renderInput("leadership", "Leadership Roles")}
            {renderInput("technicalSkills", "Technical Skills")}
            {renderInput("softSkills", "Soft Skills")}
            {renderInput("languageSkills", "Language Skills")}
          </Grid>

          {/* 9. Online Presence */}
          <Typography variant="h6" mt={3} mb={1.5}>
            9. Online Presence
          </Typography>
          <Grid container spacing={2}>
            {renderInput("linkedin", "LinkedIn (Recommendations)")}
            {renderInput("personalWebsite", "Personal Website")}
            {renderInput("socialMedia", "Professional Social Media")}
          </Grid>

          {/* 10. Financial Position & Resume */}
          <Typography variant="h6" mt={3} mb={1.5}>
            10. Financial Position & Resume
          </Typography>
          <Grid container spacing={2}>
            {renderInput("financialPosition", "Financial Position")}
            {renderResumeField()}
          </Grid>
        </>

        {isProfileCreated ? (
          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            {isFieldsDisabled ? (
              new Date(profileData?.timeline) > new Date() ? (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleEditClick}
                  disabled={isSubmitting}
                >
                  Edit Profile
                </Button>
              ) : (
                <Typography color="error" fontWeight="bold">
                  Profile editing period has expired.
                </Typography>
              )
            ) : (
              <>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    bgcolor: "#00999E",
                    "&:hover": {
                      bgcolor: "#00999E",
                    },
                    maxWidth: "300px",
                  }}
                  onClick={handleSaveChangesClick}
                  disabled={isSubmitting || Object.keys(fieldErrors).length > 0}
                  startIcon={
                    isSubmitting && (
                      <CircularProgress size={20} color="inherit" />
                    )
                  }
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCancelClick}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: "#00999E",
                "&:hover": {
                  bgcolor: "#00999E",
                },
                maxWidth: "300px",
              }}
              disabled={isSubmitting || Object.keys(fieldErrors).length > 0}
              startIcon={
                isSubmitting && <CircularProgress size={20} color="inherit" />
              }
            >
              {isSubmitting ? "Creating..." : "Create Profile"}
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default StudentCounsellorForm;
